import {customAlphabet} from 'nanoid';
import {z} from 'zod';

const Base58Alphabet = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const IdLength = 10;
const nanoid = customAlphabet(Base58Alphabet, IdLength);

export const IdField = 'id' as const;

export const ServerTimestampSentinelValue = -1;

export const MetadataField = '__meta';
export type CreateMetadata = {
  createdBy: string;
  createdTime: number;
  /**
   * Optional field to support Firestore TTL (Time-To-Live) cleanup.
   * Typed as unknown because the specific Firestore Timestamp and FieldValue
   * sentinel types differ between the Client (web SDK) and Server (admin SDK) environments,
   * while raw Date objects are also supported for writing.
   */
  expiresAt?: unknown;
};

export const DeletedByProperty = 'deletedBy';
export const DeletedProperty = 'deleted';
export const DeletedTimeProperty = 'deletedTime';

export type DeleteMetadata = {
  [DeletedByProperty]?: string;
  [DeletedProperty]: boolean;
  [DeletedTimeProperty]?: number;
};

export type UpdateMetadata = {
  updatedBy: string;
  updatedTime: number;
};

export type Metadata = CreateMetadata & DeleteMetadata & UpdateMetadata;

export const MetadataSchema = z.object({
  createdBy: z.string(),
  createdTime: z.number(),
  deleted: z.boolean().optional(),
  deletedBy: z.string().optional(),
  deletedTime: z.number().optional(),
  expiresAt: z.unknown().optional(),
  updatedBy: z.string(),
  updatedTime: z.number(),
});

export type MetadataProperty = {[MetadataField]: Metadata};
export type MetadataPropertyUpdate = {[MetadataField]: Partial<Metadata>};

export const EntitySchema = z.object({
  [IdField]: z.string().regex(new RegExp(`^[${Base58Alphabet}]{${IdLength}}-[a-z]{1,3}`)),
});

export type Entity = z.infer<typeof EntitySchema>;
export type EntityWithoutId<T extends Entity> = Omit<T, typeof IdField>;
export type EntityPrototype<T extends Entity> = EntityWithoutId<T>;
export type EntityUpdate<T extends Entity> = Partial<EntityWithoutId<T>>;
export type EntityWithMetadata<T extends Entity> = T & MetadataProperty;

export type EntityId = string;
export type EntityTypeSuffix = string;
export type IdWithEntityTypeSuffix = `${EntityId}-${EntityTypeSuffix}`;

export type IdGenerator<T extends Entity = Entity> = (prototype: EntityPrototype<T>) => EntityId;
export type TypedIdGenerator<T extends Entity = Entity> = (prototype: EntityPrototype<T>) => IdWithEntityTypeSuffix;

export const defaultUntypedIdGenerator: IdGenerator = <T extends Entity>(_prototype: EntityPrototype<T>) => nanoid();

export const defaultTypedIdGenerator: (
  entityTypeSuffix: EntityTypeSuffix,
  idGenerator?: IdGenerator,
) => TypedIdGenerator =
  (entityTypeSuffix: EntityTypeSuffix, idGenerator: IdGenerator = defaultUntypedIdGenerator) =>
  <T extends Entity>(prototype: EntityPrototype<T>) =>
    `${idGenerator(prototype)}-${entityTypeSuffix}`;
